import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Loader, ArrowLeft } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import MediaFeedCard from '../components/MediaFeedCard';
import ShareModal from '../components/ShareModal';
import SignupInviteModal from '../components/SignupInviteModal';
import AccessRequiredModal from '../components/AccessRequiredModal';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { updateLikedMediaMeta } from '../utils/metaUtils';

const LIMIT = 20;

const LikedMedia = () => {
    const { username } = useParams();
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [shareModal, setShareModal] = useState({ isOpen: false, item: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'content' });
    const [accessModal, setAccessModal] = useState({ isOpen: false, item: null });

    useEffect(() => {
        const loadInitial = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.get(`/upload/liked/${username}?limit=${LIMIT}&offset=0`);
                if (response.data.success) {
                    setItems(response.data.data || []);
                    setTotal(response.data.total || 0);
                } else {
                    setError('Unable to load liked media.');
                }
            } catch (err) {
                console.error('Error loading liked media:', err);
                setError('Unable to load liked media. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        loadInitial();
    }, [username]);

    useEffect(() => {
        const loadMeta = async () => {
            try {
                const profileRes = await api.get(`/profile/${username}`);
                const profile = profileRes.data.success ? profileRes.data.profile : null;
                await updateLikedMediaMeta(profile, username);
            } catch (err) {
                console.error('Error updating liked media meta tags:', err);
                await updateLikedMediaMeta(null, username);
            }
        };
        loadMeta();
    }, [username]);

    const loadMore = async () => {
        try {
            setLoadingMore(true);
            const response = await api.get(`/upload/liked/${username}?limit=${LIMIT}&offset=${items.length}`);
            if (response.data.success) {
                setItems(prev => [...prev, ...(response.data.data || [])]);
            }
        } catch (err) {
            console.error('Error loading more liked media:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const hasMore = items.length < total;

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader title="Liked Media" subtitle={`Everything @${username} has liked`} />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <Link
                    to={`/profile/${username}`}
                    className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to profile
                </Link>

                {loading ? (
                    <div className="text-center py-16">
                        <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                        <p className="text-gray-400">Loading liked media...</p>
                    </div>
                ) : error ? (
                    <div className="glass-card p-8 text-center text-gray-400">{error}</div>
                ) : items.length === 0 ? (
                    <div className="glass-card p-12 text-center">
                        <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-400">No liked media yet</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-4 sm:space-y-8">
                            {items.map((item) => (
                                <MediaFeedCard
                                    key={`${item.file_type}-${item.id}`}
                                    item={{ ...item, mediaType: item.file_type }}
                                    isAuthenticated={isAuthenticated}
                                    hasActiveSubscription={hasActiveSubscription}
                                    onRequireAuth={(contentType) => setSignupInvite({ isOpen: true, contentType })}
                                    onShare={(mediaItem) => setShareModal({ isOpen: true, item: mediaItem })}
                                    onAccessRequired={(mediaItem) => setAccessModal({ isOpen: true, item: mediaItem })}
                                />
                            ))}
                        </div>

                        {hasMore && (
                            <div className="mt-8 text-center">
                                <button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                                >
                                    {loadingMore ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        'Load More'
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'content' })}
                contentType={signupInvite.contentType}
            />

            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, item: null })}
                mediaTitle={shareModal.item?.title || 'Media'}
                mediaType={shareModal.item?.mediaType || 'content'}
                username={shareModal.item?.username}
                mediaId={shareModal.item?.stored_filename || shareModal.item?.original_filename || shareModal.item?.id}
                artistName={shareModal.item?.display_name || shareModal.item?.username}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, item: null })}
                accessLevel={accessModal.item?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.item ? `/view/${accessModal.item.mediaType}/${accessModal.item.username}/${accessModal.item.stored_filename || accessModal.item.original_filename || accessModal.item.id}` : null}
            />
        </div>
    );
};

export default LikedMedia;
